import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
    exportAccRatesCsvApi,
    getAccRateGridApi,
    getAccRateVintagesApi,
    getAdminUtilitiesApi,
} from '../../apis/apis'
import type { AccRateGridRow, AccRateVintage, Utility } from '../../apis/types'
import Layout from '../programs/Layout'
import QueryError from '../conductor/QueryError'
import { ComboboxAutocomplete } from '../../components/Form/ComboboxAutocomplete'
import { accRatesCsvFileName } from './components/tariffDownloadNames'
import { TariffPageHeader } from './components/TariffPageHeader'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DAY_TYPES = ['WEEKDAY', 'WEEKEND', 'HOLIDAY'] as const
type ComponentKey = 'generation' | 'delivery' | 'total'

function compareUtilityName(a: Utility, b: Utility): number {
    const left = a.shortName ?? a.name
    const right = b.shortName ?? b.name
    return left.localeCompare(right)
}

const AccRates: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const utilityCuid = searchParams.get('utilityCuid') ?? ''
    const yearParam = searchParams.get('year')
    const [utilities, setUtilities] = useState<Utility[]>([])
    const [vintages, setVintages] = useState<AccRateVintage[]>([])
    const [rows, setRows] = useState<AccRateGridRow[]>([])
    const [dayType, setDayType] = useState<(typeof DAY_TYPES)[number]>('WEEKDAY')
    const [component, setComponent] = useState<ComponentKey>('total')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const [reloadToken, setReloadToken] = useState(0)
    const [exporting, setExporting] = useState(false)
    const [utilitiesLoading, setUtilitiesLoading] = useState(true)
    const [utilitiesError, setUtilitiesError] = useState(false)
    const [pickerReloadToken, setPickerReloadToken] = useState(0)

    const [vintagesByUtility, setVintagesByUtility] = useState<ReadonlyMap<string, AccRateVintage[]>>(
        () => new Map()
    )

    useEffect(() => {
        let cancelled = false
        setUtilitiesLoading(true)
        setUtilitiesError(false)
        void (async () => {
            try {
                const ious = await getAdminUtilitiesApi('IOU')
                if (cancelled) return
                const probed = await Promise.all(
                    ious.map(async (utility) => {
                        try {
                            const loaded = await getAccRateVintagesApi(utility.id)
                            return { utility, loaded }
                        } catch {
                            return { failed: true as const }
                        }
                    })
                )
                if (cancelled) return
                const allProbesFailed =
                    ious.length > 0 && probed.every((item) => 'failed' in item)
                if (allProbesFailed) {
                    setUtilities([])
                    setVintagesByUtility(new Map())
                    setUtilitiesError(true)
                    return
                }
                const kept = probed.flatMap((item) => {
                    if ('failed' in item || item.loaded.length === 0) return []
                    return [item]
                })
                const next = new Map<string, AccRateVintage[]>()
                for (const item of kept) next.set(item.utility.id, item.loaded)
                setVintagesByUtility(next)
                setUtilities(kept.map((item) => item.utility).sort(compareUtilityName))
            } catch {
                if (cancelled) return
                setUtilities([])
                setVintagesByUtility(new Map())
                setUtilitiesError(true)
            } finally {
                if (!cancelled) setUtilitiesLoading(false)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [pickerReloadToken])

    const utilityOptions = useMemo(
        () =>
            utilities.map((utility) => ({
                id: utility.id,
                name:
                    utility.shortName != null && utility.shortName !== ''
                        ? `${utility.shortName} — ${utility.name}`
                        : utility.name,
            })),
        [utilities]
    )

    useEffect(() => {
        if (utilityOptions.length === 0) return
        const selected = utilityOptions.some((option) => option.id === utilityCuid)
        if (utilityCuid !== '' && selected) return
        const first = utilityOptions[0]
        if (first == null) return
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev)
                next.set('utilityCuid', String(first.id))
                next.delete('year')
                return next
            },
            { replace: true }
        )
    }, [utilityCuid, utilityOptions, setSearchParams])

    useEffect(() => {
        if (utilityCuid === '') {
            setVintages([])
            return
        }
        const cached = vintagesByUtility.get(utilityCuid)
        if (cached == null) {
            if (vintagesByUtility.size > 0) setVintages([])
            return
        }
        setVintages(cached)
        if (yearParam == null && cached[0] != null) {
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev)
                params.set('year', String(cached[0].interconnectionApplicationYear))
                return params
            })
        }
    }, [utilityCuid, yearParam, setSearchParams, vintagesByUtility])

    const parsedYear = yearParam != null && yearParam !== '' ? Number(yearParam) : NaN
    const year = Number.isFinite(parsedYear)
        ? parsedYear
        : vintages[0]?.interconnectionApplicationYear

    useEffect(() => {
        if (utilityCuid === '' || year == null || !Number.isFinite(year)) {
            setRows([])
            return
        }
        setLoading(true)
        setError(false)
        getAccRateGridApi(utilityCuid, year)
            .then(setRows)
            .catch(() => setError(true))
            .finally(() => setLoading(false))
    }, [utilityCuid, year, reloadToken])

    const values = useMemo(() => {
        const filtered = rows.filter((row) => row.dayType === dayType)
        const max = Math.max(0, ...filtered.map((row) => row[component] ?? 0))
        return { filtered, max }
    }, [rows, dayType, component])

    const handleExport = async (): Promise<void> => {
        if (utilityCuid === '') return
        setExporting(true)
        try {
            const blob = await exportAccRatesCsvApi(utilityCuid)
            const url = URL.createObjectURL(blob)
            const anchor = document.createElement('a')
            anchor.href = url
            const utility = utilities.find((item) => item.id === utilityCuid)
            const years = vintages.map((vintage) => vintage.interconnectionApplicationYear)
            anchor.download = accRatesCsvFileName(utility?.shortName, years)
            anchor.click()
            URL.revokeObjectURL(url)
        } finally {
            setExporting(false)
        }
    }

    return (
        <Layout
            title={
                <TariffPageHeader
                    showSectionTabs
                    title="NEM3 export rates"
                    subtitle="Hourly Avoided Cost Calculator rates by interconnection year. CCA-enrolled accounts credit Delivery only."
                />
            }
        >
            <div className="mb-4 flex flex-wrap items-end gap-3">
                {utilitiesLoading ? (
                    <div
                        className="h-10 min-w-[28rem] animate-pulse rounded-md bg-slate-200 dark:bg-slate-700"
                        aria-hidden
                    />
                ) : utilitiesError ? (
                    <QueryError
                        message="Failed to load ACC utilities."
                        onRetry={() => {
                            setUtilitiesError(false)
                            setPickerReloadToken((token) => token + 1)
                        }}
                    />
                ) : utilities.length === 0 ? (
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                        No IOUs have loaded ACC vintages.
                    </p>
                ) : (
                    <div className="min-w-[28rem]">
                        <ComboboxAutocomplete
                            label="Utility"
                            labelPlural="Utilities"
                            value={utilityCuid === '' ? null : utilityCuid}
                            handleChange={(id) => {
                                setSearchParams((prev) => {
                                    const next = new URLSearchParams(prev)
                                    if (id == null) return prev
                                    next.set('utilityCuid', String(id))
                                    next.delete('year')
                                    return next
                                })
                            }}
                            data={utilityOptions}
                            wildCardDisabled
                            isNotFilter
                            placeholder="Search utilities..."
                            description="Includes IOUs that have loaded ACC vintages."
                        />
                    </div>
                )}
                {vintages.length > 1 ? (
                    <label className="text-sm">
                        <span className="mb-1 block text-slate-600 dark:text-slate-300">
                            Interconnection year
                        </span>
                        <select
                            className="min-w-[7.5rem] rounded-md border border-slate-300 py-1.5 pl-3 pr-8 dark:border-slate-600 dark:bg-slate-900"
                            value={year ?? ''}
                            onChange={(event) => {
                                const value = event.target.value
                                setSearchParams((prev) => {
                                    const next = new URLSearchParams(prev)
                                    if (value === '') next.delete('year')
                                    else next.set('year', value)
                                    return next
                                })
                            }}
                        >
                            {vintages.map((vintage) => (
                                <option
                                    key={vintage.interconnectionApplicationYear}
                                    value={vintage.interconnectionApplicationYear}
                                >
                                    {vintage.interconnectionApplicationYear}
                                </option>
                            ))}
                        </select>
                    </label>
                ) : null}
                <div className="inline-flex rounded-md shadow-sm" role="tablist" aria-label="Day type">
                    {DAY_TYPES.map((type, index) => (
                        <button
                            key={type}
                            type="button"
                            role="tab"
                            aria-selected={dayType === type}
                            onClick={() => setDayType(type)}
                            className={[
                                'border px-3 py-1.5 text-sm',
                                index === 0 ? 'rounded-l-md' : '-ml-px',
                                index === DAY_TYPES.length - 1 ? 'rounded-r-md' : '',
                                dayType === type
                                    ? 'border-indigo-600 bg-indigo-600 text-white'
                                    : 'border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-900',
                            ].join(' ')}
                        >
                            {type === 'WEEKDAY' ? 'Weekday' : type === 'WEEKEND' ? 'Weekend' : 'Holiday'}
                        </button>
                    ))}
                </div>
                <div className="inline-flex rounded-md shadow-sm" role="group" aria-label="Component">
                    {(
                        [
                            ['generation', 'Generation'],
                            ['delivery', 'Delivery'],
                            ['total', 'Total'],
                        ] as Array<[ComponentKey, string]>
                    ).map(([key, label], index) => (
                        <button
                            key={key}
                            type="button"
                            onClick={() => setComponent(key)}
                            className={[
                                'border px-3 py-1.5 text-sm',
                                index === 0 ? 'rounded-l-md' : '-ml-px',
                                index === 2 ? 'rounded-r-md' : '',
                                component === key
                                    ? 'border-indigo-600 bg-indigo-600 text-white'
                                    : 'border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-900',
                            ].join(' ')}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>
            {error ? (
                <QueryError
                    message="Failed to load ACC rates."
                    onRetry={() => {
                        setError(false)
                        setReloadToken((token) => token + 1)
                    }}
                />
            ) : null}
            {loading ? <div className="h-64 animate-pulse rounded-md bg-slate-200 dark:bg-slate-700" /> : null}
            {!loading && !error && utilityCuid !== '' ? (
                <div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full border-collapse text-xs">
                            <thead>
                                <tr>
                                    <th className="px-1 py-1 text-left">Hour</th>
                                    {MONTHS.map((month) => (
                                        <th key={month} className="px-1 py-1 text-right">
                                            {month}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {Array.from({ length: 24 }, (_, hour) => (
                                    <tr key={hour}>
                                        <td className="px-1 py-0.5 font-mono text-slate-500">{hour}</td>
                                        {MONTHS.map((_, monthIndex) => {
                                            const cell = values.filtered.find(
                                                (row) => row.month === monthIndex + 1 && row.hour === hour
                                            )
                                            const value = cell?.[component] ?? null
                                            const intensity =
                                                value != null && values.max > 0 ? value / values.max : 0
                                            return (
                                                <td
                                                    key={`${monthIndex}-${hour}`}
                                                    className="px-1 py-0.5 text-right font-mono"
                                                    title={
                                                        value != null
                                                            ? `$${value.toFixed(6)}/kWh`
                                                            : 'No rate'
                                                    }
                                                    style={{
                                                        backgroundColor:
                                                            value == null
                                                                ? undefined
                                                                : `rgba(79, 70, 229, ${0.08 + intensity * 0.55})`,
                                                    }}
                                                >
                                                    {value != null ? value.toFixed(3) : '—'}
                                                </td>
                                            )
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center justify-end gap-3">
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            All loaded vintages and day types. Generation and delivery as separate
                            rows. Total is their sum.
                        </p>
                        <button
                            type="button"
                            disabled={utilityCuid === '' || exporting}
                            onClick={() => void handleExport()}
                            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                        >
                            {exporting ? 'Exporting…' : 'Download CSV'}
                        </button>
                    </div>
                </div>
            ) : null}
        </Layout>
    )
}

export default AccRates
